import { isValidPhoneNumber } from "libphonenumber-js";
import { Address } from "@./address/address";
import { AddressValidationErrorPostcodeType } from "@./address/address.types";
import { validateAndSanitiseGBPostcode } from "@./address/validation";
import dayjs from "dayjs";
import { DEFAULT_DATE_AS_DISPLAY_FORMAT } from "libs/utils/misc/src/dates";

export type ValidationRule =
  | "isRequired"
  | "isEmail"
  | "isNumber"
  | "isNotEmptyArray"
  | "isBoolean"
  | "isTrue"
  | "isValidUKPhoneNumber"
  | "isValidUKPostcode"
  | "isValidPostcodeFormat"
  | "isDeliverablePostcode"
  | { type: "isBetween"; min: number; max: number }
  | { type: "isDateBefore"; date: string };

export type ValidatableValue = string | number | boolean | null | undefined | string[] | Date;

export const validationRules: Record<
  Exclude<ValidationRule, { type: "isBetween"; min: number; max: number } | { type: "isDateBefore"; date: string }>,
  (value: ValidatableValue) => boolean
> = {
  isRequired: (value) => {
    if (value === null || value === undefined) { return false; }
    if (typeof value === "string") { return value.trim() !== ""; }
    if (typeof value === "number") { return !Number.isNaN(value); }
    if (Array.isArray(value)) { return value.length > 0; }
    return true;
  },

  isEmail: (value) => typeof value === "string" &&
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value),

  isNumber: (value) => typeof value === "number" && !Number.isNaN(value),

  isNotEmptyArray: (value) => Array.isArray(value) && value.length > 0,

  isBoolean: (value) => typeof value === "boolean",

  isTrue: (value) => value === true,

  isValidUKPhoneNumber: (value) => {
    if (typeof value !== "string") { return false; }
    return isValidPhoneNumber(value, "GB");
  },

  isValidUKPostcode: (value) => {
    if (typeof value !== "string") { return false; }
    const { postcodeCheck } = Address();
    const result = postcodeCheck(value);
    return result.errorType === null;
  },

  isValidPostcodeFormat: (value) => {
    if (typeof value !== "string") { return false; }
    const result = validateAndSanitiseGBPostcode(value, true);
    return result.errorType !== AddressValidationErrorPostcodeType.BadPostcodeFormat;
  },

  isDeliverablePostcode: (value) => {
    if (typeof value !== "string") { return false; }
    const result = validateAndSanitiseGBPostcode(value);
    return result.errorType !== AddressValidationErrorPostcodeType.DoNotDeliverToPostcode;
  },
};

export const validationMessages: Record< Exclude<ValidationRule, { type: "isBetween"; min: number; max: number } | { type: "isDateBefore"; date: string }>, (fieldName?: string) => string> = {
  isRequired: (fieldName) => (fieldName ? "isFieldNameRequired" : "isRequired"),
  isEmail: () => "isEmail",
  isNumber: () => "isNumber",
  isNotEmptyArray: () => "isNotEmptyArray",
  isBoolean: () => "isBoolean",
  isTrue: () => "isTrue",
  isValidUKPhoneNumber: () => "isValidUKPhoneNumber",
  isValidUKPostcode: () => "isValidUKPostcode",
  isValidPostcodeFormat: () => "isValidPostcodeFormat",
  isDeliverablePostcode: () => "isDeliverablePostcode",
};

export function validateDynamicRule(
  rule: ValidationRule,
  value: ValidatableValue,
): string | null {
  if (typeof rule !== "object") {
    return null;
  }
  if (rule.type === "isBetween") {
    const num = typeof value === "number" ? value : Number.NaN;
    if (Number.isNaN(num) || num < rule.min || num > rule.max) {
      return `Value must be between ${rule.min} and ${rule.max}`;
    }
  }
  if (rule.type === "isDateBefore") {
    const isStringDate = typeof value === "string";
    if (!isStringDate || !dayjs(value).isBefore(rule.date)) {
      return `Must be before ${dayjs(rule.date).format(DEFAULT_DATE_AS_DISPLAY_FORMAT)}`;
    }
  }
  return null;
}
